import { classNames } from '@repo/ui/src/utils/classNames';
import { RecipeJsonLd } from 'next-seo';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { convertMinutesToISO8601 } from '@repo/ui/src/functions/recipe-functions';
import { MediaType } from '@repo/ui/src/types/image';
import {
  YoutubeLinkProps,
  AuthorProps,
  RecipeStepProps,
  RecipeDataProps,
  RecipeMetaSectionProps,
  IngredientSummaryProps,
  RatingSummary,
} from '@repo/ui/src/types';

export interface RecipeSeoProps {
  youtubeLink?: YoutubeLinkProps;
  author?: AuthorProps;
  assets?: Array<MediaType>;
  recipeMetaSection?: RecipeMetaSectionProps;
  steps?: Array<RecipeStepProps>;
  _data?: Pick<RecipeDataProps, 'ingredientSummary' | 'ratingsAndReviews'>;
  ratingSummary?: RatingSummary;
  ingredientSummary?: IngredientSummaryProps;
}

const RecipeSeo = ({
  youtubeLink,
  author,
  steps,
  recipeMetaSection,
  _data,
  assets,
  ratingSummary,
  ingredientSummary,
}: RecipeSeoProps) => {
  const category = recipeMetaSection?.categoryInformation?.relatedCategories?.[0] ?? '';
  const ingredientGroup = ingredientSummary
    ? ingredientSummary.groups
    : _data?.ingredientSummary?.groups;
  const ratingValue = ratingSummary
    ? ratingSummary?.average
    : _data?.ratingsAndReviews?.ratingSummary?.average;
  const ratingCount = ratingSummary
    ? ratingSummary?.count
    : _data?.ratingsAndReviews?.ratingSummary?.count;
  const ingredients = ingredientGroup?.map((group) =>
    group?.ingredients.map((ingredient) => {
      return !ingredient?.imperial
        ? ingredient?.ingredient?.name
        : classNames(
            ingredient.imperial?.measurementDisplay,
            ingredient.unitOfMeasure,
            ingredient?.ingredient?.name,
          );
    }),
  );
  const imgUrl =
    assets && assets.length > 0
      ? buildImageSrc(assets[0], 500, 1, 1)
      : buildImageSrc(null, 500, 1, 1);

  return (
    <RecipeJsonLd
      useAppDir
      scriptId="jsonld-recipe"
      name={recipeMetaSection?.title || ''}
      description={recipeMetaSection?.description || ''}
      authorName={author?.name ?? ''}
      prepTime={convertMinutesToISO8601(recipeMetaSection?.prepTime)}
      cookTime={convertMinutesToISO8601(recipeMetaSection?.cookTime)}
      keywords={recipeMetaSection?.keywords?.join(' ') ?? ''}
      category={category}
      images={[imgUrl]}
      ingredients={ingredients?.flat() ?? []}
      instructions={
        steps?.map(({ title, instruction }) => ({
          name: title,
          text: instruction,
        })) ?? []
      }
      aggregateRating={
        ratingCount &&
        ratingCount !== 0 && {
          ratingValue: ratingValue,
          reviewCount: ratingCount,
        }
      }
      video={
        youtubeLink && {
          name: youtubeLink?.name,
          contentUrl: youtubeLink?.contentUrl,
          embedUrl:
            youtubeLink?.embedUrl ||
            `https://www.youtube.com/embed/${
              youtubeLink?.youtubeId ?? youtubeLink?.externalVideoId
            }`,
          duration: convertMinutesToISO8601(youtubeLink?.duration / 60),
          thumbnailUrls: youtubeLink?.thumbnailUrl,
        }
      }
    />
  );
};

export default RecipeSeo;
